import { Temporal } from 'temporal-polyfill';
import type { ClassSeries, Location, RecurrenceRule } from './types';

export interface FieldError {
  readonly field: string;
  readonly message: string;
}

export type ValidationResult =
  { readonly ok: true } | { readonly ok: false; readonly errors: FieldError[] };

const MIN_DURATION_MINUTES = 5;
const MAX_DURATION_MINUTES = 8 * 60;
const HEX_COLOR = /^#[0-9a-f]{6}$/iu;
const WALL_CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/u;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/u;

function result(errors: FieldError[]): ValidationResult {
  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}

/**
 * IANA zone check. Temporal throws on an unknown identifier, which is the only
 * reliable way to validate without shipping a zone list.
 */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    Temporal.Now.instant().toZonedDateTimeISO(timeZone);
    return true;
  } catch {
    return false;
  }
}

export function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) {
    return false;
  }
  try {
    // Rejects impossible dates like 2026-02-30 that pass the shape test.
    return Temporal.PlainDate.from(value, { overflow: 'reject' }).toString() === value;
  } catch {
    return false;
  }
}

export type LocationDraft = Pick<Location, 'name' | 'address' | 'timeZone'>;

export function validateLocation(draft: LocationDraft): ValidationResult {
  const errors: FieldError[] = [];

  if (draft.name.trim().length === 0) {
    errors.push({ field: 'name', message: 'Give the place a name.' });
  }
  if (draft.name.length > 80) {
    errors.push({ field: 'name', message: 'Name must be 80 characters or fewer.' });
  }
  if (!isValidTimeZone(draft.timeZone)) {
    errors.push({ field: 'timeZone', message: `"${draft.timeZone}" is not a known timezone.` });
  }

  return result(errors);
}

export type SeriesDraft = Pick<
  ClassSeries,
  | 'title'
  | 'locationId'
  | 'color'
  | 'startTime'
  | 'durationMinutes'
  | 'recurrence'
  | 'startDate'
  | 'endDate'
  | 'notes'
  | 'isSub'
>;

export function validateSeries(draft: SeriesDraft): ValidationResult {
  const errors: FieldError[] = [];

  if (draft.title.trim().length === 0) {
    errors.push({ field: 'title', message: 'Give the class a name.' });
  }
  if (draft.title.length > 80) {
    errors.push({ field: 'title', message: 'Name must be 80 characters or fewer.' });
  }
  if (draft.locationId.trim().length === 0) {
    errors.push({ field: 'locationId', message: 'Pick a place.' });
  }
  if (!HEX_COLOR.test(draft.color)) {
    errors.push({ field: 'color', message: 'Colour must be a hex value like #6b4f8a.' });
  }
  if (!WALL_CLOCK.test(draft.startTime)) {
    errors.push({ field: 'startTime', message: 'Start time must be a 24-hour HH:MM value.' });
  }
  if (
    !Number.isInteger(draft.durationMinutes) ||
    draft.durationMinutes < MIN_DURATION_MINUTES ||
    draft.durationMinutes > MAX_DURATION_MINUTES
  ) {
    errors.push({
      field: 'durationMinutes',
      message: `Duration must be a whole number between ${MIN_DURATION_MINUTES} and ${MAX_DURATION_MINUTES} minutes.`,
    });
  }
  if (!isValidIsoDate(draft.startDate)) {
    errors.push({ field: 'startDate', message: 'Start date must be a real YYYY-MM-DD date.' });
  }
  if (draft.endDate !== null) {
    if (!isValidIsoDate(draft.endDate)) {
      errors.push({ field: 'endDate', message: 'End date must be a real YYYY-MM-DD date.' });
    } else if (isValidIsoDate(draft.startDate) && draft.endDate < draft.startDate) {
      errors.push({ field: 'endDate', message: 'End date cannot precede the start date.' });
    }
  }

  errors.push(...validateRecurrence(draft.recurrence));
  return result(errors);
}

export function validateRecurrence(rule: RecurrenceRule): FieldError[] {
  switch (rule.freq) {
    case 'once':
    case 'daily':
      return [];
    case 'weekly':
    case 'biweekly':
      return rule.byWeekday.length === 0
        ? [{ field: 'recurrence', message: 'Pick at least one weekday.' }]
        : rule.byWeekday.some((day) => day < 1 || day > 7)
          ? [{ field: 'recurrence', message: 'Weekdays must be 1 (Mon) through 7 (Sun).' }]
          : [];
    case 'monthly':
      if (rule.mode === 'byMonthDay') {
        return Number.isInteger(rule.monthDay) && rule.monthDay >= 1 && rule.monthDay <= 31
          ? []
          : [{ field: 'recurrence', message: 'Day of month must be between 1 and 31.' }];
      }
      return rule.weekday >= 1 && rule.weekday <= 7 && [1, 2, 3, 4, -1].includes(rule.nth)
        ? []
        : [{ field: 'recurrence', message: 'Pick a valid weekday and position in the month.' }];
    default: {
      const exhaustive: never = rule;
      return [{ field: 'recurrence', message: `Unsupported rule: ${JSON.stringify(exhaustive)}` }];
    }
  }
}

/** Convenience for the UI: which field, if any, has an error. */
export function errorFor(result: ValidationResult, field: string): string | undefined {
  if (result.ok) {
    return undefined;
  }
  return result.errors.find((error) => error.field === field)?.message;
}
